import { Component, Input, OnInit } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import { Sort } from "@angular/material/sort";
import { trackByProp, trackId } from "@app/_helpers/ngFor";
import type { WorkingHourViolation } from "@app/_helpers/rulebreakUtil";
import {
	getComegoFutureLockedDate,
	getComegoLockedDate,
	getTimesFutureLockDate,
	getTimesLockedDate,
} from "@app/_helpers/timeLock";
import { createRxValue, parseScheduleStats } from "@app/_helpers/utils";
import { COMEGO_ICON_MAP } from "@app/components/come-and-go-create-dialog/come-and-go-utils";
import {
	ComeAndGoMultiUpdateDialogComponent,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-multi-update-dialog/come-and-go-update-dialog.component";
import {
	ComeAndGoUpdateDialogComponent,
	ComeGoUpdateDialogData,
} from "@app/components/come-and-go-update-dialog/come-and-go-update-dialog.component";
import { WorkspaceUserDialogComponent } from "@app/components/workspace/update-user-dialog/update-user-dialog.component";
import { ReportsService } from "@app/pages/reports-page/reports.service";
import { MatRightSideSheet } from "@app/shared/mat-right-side-sheet/mat-right-side-sheet";
import { TimeTrackerCalendarUpdateDialogComponent } from "@app/shared/time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { ComegoTime, Logger, ProjectsQuery, Time, UserSettingsQuery } from "@timeghost/web-api";
import { CalendarEvent } from "calendar-utils";
import { addDays, isSameDay, isValid, parseISO, startOfDay, startOfWeek } from "date-fns/esm";
import { omit } from "lodash-es";
import { combineLatest, map, tap } from "rxjs";
import { firstBy } from "thenby";
import { ReportItem } from "../../utils";
import { SchedulesPageComponent } from "../schedules-page.component";

type ScheduleTableItem = {
	enabled: boolean;
	summed: number;
	overtime: number;
	missing: number;
	schedules: ReturnType<typeof parseScheduleStats>[];
};
type ScheduleTable = Record<string, ScheduleTableItem>;
const log = new Logger("SchedulesTableComponent");

/** Calendar days (start-of-day timestamps) to show when filtering by rulebreaks: full day per violation + adjacent days for `rest`. */
function collectRulebreakContextDayStartTimestamps(
	times: { start: Date; violations?: WorkingHourViolation[] }[]
): Date[] {
	let dayStarts: Date[] = [];
	for (const entry of times) {
		if (!entry.violations?.length) continue;
		for (const v of entry.violations) {
			if (v.type === "rest") {
				dayStarts = dayStarts.concat(startOfDay(addDays(entry.start, -1))).uniq();
				dayStarts = dayStarts.concat(startOfDay(entry.start)).uniq();
			} else {
				dayStarts = dayStarts.concat(startOfDay(entry.start)).uniq();
			}
		}
	}
	return dayStarts;
}
@Component({
	selector: "tg-schedules-table",
	templateUrl: "./schedules-table.component.html",
	styleUrls: ["./schedules-table.component.scss"],
})
export class SchedulesTableComponent implements OnInit {
	readonly iconMap = COMEGO_ICON_MAP;
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private schedulesPage: SchedulesPageComponent,
		private dialog: MatDialog,
		private sheet: MatRightSideSheet,
		private projectsQuery: ProjectsQuery,
		private translate: TranslateService,
		private reportsService: ReportsService
	) {}
	private _data = createRxValue<ReportItem<ComegoTime>[]>();
	readonly workspaceScheduleEnabled = this.userSettingsQuery.select((x) => x.workspace.schedules.workspace.enabled);
	readonly data$Visible = this.schedulesPage.data$Visible;
	readonly activeSort$ = createRxValue<Sort>({ active: "schedules.difference", direction: "asc" });
	get activeSort() {
		return this.activeSort$.value;
	}
	get rulebreaksAudit() {
		return this.schedulesPage.auditRulebreaks.value;
	}
	readonly now = startOfWeek(new Date(), { weekStartsOn: 1 });
	readonly data$ = combineLatest([
		this.schedulesPage.data$,
		this.userSettingsQuery.select(),
		this.activeSort$.asObservable(),
		this.schedulesPage.users$,
		this.reportsService.range$,
		this.schedulesPage.auditRulebreaks.asObservable(),
	]).pipe(
		map(([data, user, sort, users, range, onlyRulebreaks]) => {
			let renderData = Object.entries(data).map(([userid, x]) => {
				return {
					...x,
					user: user.workspace.users.find((x) => x.id === userid),
					userId: userid,
				};
			});

			if (sort?.active) {
				log.debug("sort", sort);
				const sortName = sort.active;
				const sortDir = sort.direction as SortOrder;
				if (sortName === "name") renderData = renderData.sort(firstBy((x) => x.user.name, sortDir));
				else if (sortName === "schedules") renderData = renderData.sort(firstBy((x) => x.summed, sortDir));
				else if (sortName === "schedules.overtime")
					renderData = renderData.sort(firstBy((x) => x.overtime, sortDir));
				else if (sortName === "schedules.future")
					renderData = renderData.sort(firstBy((x) => x.future, sortDir));
				else if (sortName === "schedules.missing")
					renderData = renderData.sort(firstBy((x) => x.missing, sortDir));
				else renderData = renderData.sort(firstBy((x) => x.difference, sortDir || "asc"));
			} else {
				renderData = renderData.sort(firstBy((x) => x.difference, "asc"));
			}
			if (onlyRulebreaks) {
				const filteredData = renderData
					.filter((d: any) => d.errors?.length)
					.map(({ times, ...d }: any) => {
						const contextDays = collectRulebreakContextDayStartTimestamps(times);
						return {
							...d,
							times: times.filter((x: any) => contextDays.some((d) => isSameDay(d, x.start))),
						} as any;
					});
				return filteredData;
			}
			return renderData;
		}),
		tap((x) => log.debug("schedules-table-data", x))
	);
	readonly isLoading$ = this.reportsService.timeLoading$;
	@Input()
	set data(values: any) {
		this._data.next(values);
	}
	readonly loadingRows = new Array(5).fill(1);
	ngOnInit(): void {}
	async editItem(item: Time | (ComegoTime & { recording: boolean })) {
		if ("projectId" in item) {
			this.dialog
				.open(TimeTrackerCalendarUpdateDialogComponent, {
					data: <CalendarEvent>{
						title: item.name,
						start: new Date(item.start),
						end: new Date(item.end),
						meta: { time: item },
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => {
					if (!!x) {
						this.reportsService.reload();
					}
				});
		} else {
			const comego = item as unknown as ReportItem<ComegoTime>;
			this.dialog
				.open(ComeAndGoMultiUpdateDialogComponent, {
					data: {
						time: {
							value: [{ ...comego, user: { id: comego.userId } } as unknown as ComegoTime],
							date: new Date(comego.start),
							refetchFromDate: true,
						},
						disabledFields: ["user"],
						forceRecreateDay: true,
					} as WorkingHoursUpdateData,
				})
				.afterClosed()
				.toPromise()
				.then((x) => {
					if (!!x) {
						this.reportsService.reload();
					}
				});
		}
	}
	changeDataSort(event: Sort) {
		this.activeSort$.next(event);
	}

	contextMenuPosition = { x: "0px", y: "0px" };
	openContextMenu(event: MouseEvent | Event, trigger: MatMenuTrigger, data: any) {
		event.stopPropagation(), event.preventDefault();
		if (event instanceof MouseEvent) {
			this.contextMenuPosition.x = event.clientX + "px";
			this.contextMenuPosition.y = event.clientY + "px";
		} else {
			const { left, top } = (event.currentTarget! as HTMLElement)?.getClientRects()[0];
			this.contextMenuPosition.x = left + "px";
			this.contextMenuPosition.y = top + "px";
		}
		trigger.menuData = data;
		trigger.menu.focusFirstItem("mouse");
		trigger.openMenu();
	}
	setFilterToUser(userId: string) {
		this.reportsService.teamFilter.update(() => [userId]);
	}
	openUserSettings(userId: string) {
		const user = this.userSettingsQuery.getValue();
		if (!user.workspace.users?.find((d) => d.admin && d.id === user.id)) return;
		return this.sheet.open(WorkspaceUserDialogComponent, {
			data: {
				userId,
			},
		});
	}
	lookingAtSubordinate(itemUserId: any) {
		if (this.userSettingsQuery.getValue().id != itemUserId) return true;
		return false;
	}

	isLocked(date: Date, item: ReportItem<ComegoTime>) {
		const currentUser = this.userSettingsQuery.getValue();
		if (item?.type?.name == "absence") return false;
		if ("projectId" in item) {
			const projectId = item.projectId;
			const project = this.projectsQuery.getAll().find((x) => x.id == projectId);
			if (project) {
				const minDate = getTimesLockedDate(currentUser, project);
				const maxDate = getTimesFutureLockDate(currentUser, project);
				if (minDate && maxDate) {
					if (date < minDate || date > maxDate) return true;
					else return false;
				} else if (minDate) {
					if (date < minDate) return true;
				} else if (maxDate) {
					if (date > maxDate) return true;
				} else return false;
			} else false;
		} else if ("type" in item) {
			const minDate = getComegoLockedDate(currentUser, currentUser.id);
			const maxDate = getComegoFutureLockedDate(currentUser, currentUser.id);
			if (minDate && maxDate) {
				if (date < minDate || date > maxDate) return true;
				else return false;
			} else if (minDate) {
				if (date < minDate) return true;
			} else if (maxDate) {
				if (date > maxDate) return true;
			} else return false;
		}
	}
	readonly trackId = trackId;
	readonly trackByUserId = trackByProp("userId");
}
